import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context.ts';

export type tAlertSurfaceBoundaryProps = {
  /** Which alert-consuming surface this wraps, for the Sentry tag. */
  surface: 'wide' | 'bell' | 'in-page';
  children: ReactNode;
};

/**
 * Tier 2 of the two-tier containment (RET-1871, pattern: `Chat.component.tsx`).
 *
 * Wraps an individual alert-consuming surface (`AlertBanner`, `AlertBell` in
 * the navbar shell; `AlertInPage` inside a page since RET-1957). Unlike Tier 1,
 * this fallback renders NOTHING: an alert surface that "failed" must not itself
 * become an alert, and no retry affordance is offered for a surface whose only
 * job is to render quietly or not at all. Siblings outside this boundary's
 * subtree (the navbar's other banners, or the page's own content) are
 * unaffected by the catch — which is the whole point of mounting the in-page
 * host inside one: a throwing alert must never take the funnel board with it.
 */
export default function AlertSurfaceBoundary(props: tAlertSurfaceBoundaryProps) {
  const { currentWorkspace } = useWorkspaceContext();

  return (
    <Sentry.ErrorBoundary
      fallback={() => <></>}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'alert_surface');
        scope.setTag('alert_surface', props.surface);
        scope.setTag('workspace_id', currentWorkspace?.id ?? 'unknown');
        scope.setLevel('error');
      }}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
}
